import { useDispatch, useSelector } from 'react-redux'
import styles from './ChatPageV2.module.scss'
import { Avatar } from '@mui/material'
import DropdownButton from '../../components/atoms/dropdown/Dropdown'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { formatDateAndTimeForChat } from '../../util/formatDateAndTime'
import {
	fetchGroupMembers,
	fetchGroupMessages,
	sendGroupMessage,
	updateCurrentGroupMessages,
	markGroupMessagesAsRead,
	updateGroupUnreadCount,
	updateGroupListMessage,
} from '../../features/groupSlice'
import { getNotifications, countUnreadNotifications, getNotificationsKazoo, countUnreadNotificationsKazoo } from '../../features/notificationSlice'
import { toastHelper } from '../../util/toastHelper'
import { isFreeTierVendorOrPlanner } from '../../utils/chatFreeTier'

export const GroupChatWindow = ({ handleOptionSelect, closeSidebar, me }) => {
	const dispatch = useDispatch()

	const currentSelectedGroup = useSelector(
		(s) => s.group.currentSelectedGroup
	)
	const messages = useSelector((s) => s.group.messages)
	const groupMembers = useSelector((s) => s.group.groupMembers).reduce(
		(acc, member) => {
			acc[member._id] = { ...member, _id: undefined }
			return acc
		},
		{}
	)

	const { kazooId } = useParams()

	const socket = useSelector((s) => s.auth.socket)
	const onlineUsers = useSelector((s) => s.auth.onlineUsers)
	const userStatuses = useSelector((s) => s.auth.userStatuses)
	const accountType = useSelector((s) => s.auth.accountType)
	const payment = useSelector((s) => s.auth.user?.payment)

	const [text, setText] = useState('')
	const [loading, setLoading] = useState(false)
	const [selectedFile, setSelectedFile] = useState(null)
	const [uploading, setUploading] = useState(false)
	const [uploadingPreview, setUploadingPreview] = useState(null)
	const fileInputRef = useRef(null)

	const removeSelectedFile = () => {
		setSelectedFile(null)
		if (uploadingPreview?.url) {
			URL.revokeObjectURL(uploadingPreview.url)
		}
		setUploadingPreview(null)
		if (fileInputRef.current) fileInputRef.current.value = ''
	}

	const handleFileSelect = (e) => {
		if (uploading) return;
		const file = e.target.files[0]
		if (!file) return

		const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
		if (file.size > MAX_FILE_SIZE) {
			toastHelper('File size should be maximum 5 MB', 'error')
			removeSelectedFile()
			return
		}

		const acceptedMimeTypes = [
			'image/png',
			'image/jpeg',
			'image/gif',
			'image/svg+xml',
			'video/mp4',
			'video/quicktime',
			'application/pdf',
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			'text/csv',
		]

		if (!acceptedMimeTypes.includes(file.mimetype) && !acceptedMimeTypes.includes(file.type)) {
			toastHelper('Invalid file type. Allowed: PNG, JPEG, GIF, SVG, MP4, MOV, PDF, XLSX, CSV.', 'error')
			removeSelectedFile()
			return
		}

		// Create preview URL for images/videos
		if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
			const previewUrl = URL.createObjectURL(file)
			console.log('Preview created:', { url: previewUrl, type: file.type })
			setUploadingPreview({ url: previewUrl, type: file.type })
		}

		setSelectedFile(file)
	}

	const handleSendChat = async (e) => {
		e.preventDefault()
		if (uploading) return;
		const message = text.trim()
		if (!message && !selectedFile) return

		if (selectedFile) {
			const formData = new FormData()
			formData.append('groupId', currentSelectedGroup._id)
			formData.append('text', message)
			formData.append('image', selectedFile) // 'image' field for multer

			console.log('Starting upload, preview:', uploadingPreview)
			setUploading(true)
			try {
				const [uploadResult] = await Promise.all([
					dispatch(sendGroupMessage(formData)).unwrap(),
					new Promise(resolve => setTimeout(resolve, 500))
				])
				console.log('Upload complete')
			} catch (error) {
				console.error('Upload failed:', error)
			} finally {
				setUploading(false)
				removeSelectedFile()
			}
		} else {
			const tempId = Date.now().toString()
			dispatch(
				sendGroupMessage({
					groupId: currentSelectedGroup._id,
					text: message,
					tempId,
					senderId: me._id
				})
			)
		}

		setText('')
	}

	// Handle chat window open/close and mark messages as read
	useEffect(() => {
		if (!currentSelectedGroup?._id || !socket) return

		console.log('🔵 Opening group chat with:', currentSelectedGroup._id)

		// Tell server group chat is open
		socket.emit('groupChatWindowOpen', currentSelectedGroup._id)

		// Mark all messages as read
		dispatch(markGroupMessagesAsRead({
			groupId: currentSelectedGroup._id
		})).then(() => {
			if (kazooId) {
				dispatch(countUnreadNotificationsKazoo(kazooId))
				dispatch(getNotificationsKazoo(kazooId))
			}
			dispatch(countUnreadNotifications())
			dispatch(getNotifications())
		})

		return () => {
			console.log('🔴 Closing group chat with:', currentSelectedGroup._id)
			dispatch(markGroupMessagesAsRead({
				groupId: currentSelectedGroup._id
			}))
			socket.emit('groupChatWindowClose')
		}
	}, [currentSelectedGroup?._id, socket, dispatch])

	// Fetch messages and members when group is selected
	useEffect(() => {
		if (!currentSelectedGroup?._id) return
		setLoading(true)
		dispatch(fetchGroupMembers(currentSelectedGroup._id))
		dispatch(fetchGroupMessages(currentSelectedGroup._id)).finally(() =>
			setLoading(false)
		)
	}, [dispatch, currentSelectedGroup?._id])

	// Listen for new group messages
	useEffect(() => {
		if (!socket) return

		const handler = (data) => {
			// Handle both old format (just message) and new format (object with message, unreadCount, etc)
			const message = data?.message || data
			const unreadCount = data?.unreadCount
			const isViewingChat = data?.isViewingChat

			// Update group list with latest message
			dispatch(updateGroupListMessage({ message }))

			// If message is from current group
			if (message?.groupId === currentSelectedGroup?._id) {
				dispatch(updateCurrentGroupMessages(message))

				// If chat is open, it should already be marked as read
				if (isViewingChat) {
					console.log('✅ Group message auto-marked as read (chat is open)')
				}
			} else if (message?.groupId && unreadCount !== undefined) {
				// Message from different group - update unread count
				console.log('📬 Message from other group, updating unread count:', unreadCount)
				dispatch(updateGroupUnreadCount({
					groupId: message.groupId,
					count: unreadCount
				}))
			}
		}

		socket.on('newGroupMessage', handler)
		return () => socket.off('newGroupMessage', handler)
	}, [dispatch, socket, currentSelectedGroup?._id])

	return (
		<>
			{currentSelectedGroup ? (
				<>
					<div className={styles.chatHeader}>
						<div
							onClick={closeSidebar}
							className={styles.arrowBack}
							style={{
								cursor: 'pointer',
								marginRight: '12px',
							}}
						>
							<img
								src="/images/ArrowLeftChat.svg"
								alt="arrow-left"
							/>
						</div>
						<Avatar
							src={currentSelectedGroup.groupProfilePic}
							alt="avatar"
							className={styles.avatar}
						/>
						<div className={styles.userInfo}>
							<h3 className={styles.userName}>
								{currentSelectedGroup.name}
							</h3>
							<p>{Object.keys(groupMembers).length} Members</p>
						</div>
						<DropdownButton
							label={<img src="/images/more.svg" alt="more" />}
							showArrow={false}
							variant="outlined"
							options={[
								{
									label: 'View Group',
									value: 'view_group',
									imgBefore: true,
									imgSrc: '/images/info-circle.svg',
								},
								{
									label: currentSelectedGroup?.isMuted ? 'Unmute Notifications' : 'Mute Notifications',
									value: 'mute',
									imgBefore: true,
									imgSrc: currentSelectedGroup?.isMuted ? '/images/Mute-Notification.svg' : '/images/bell.svg',
								},
								{
									label: 'Leave',
									value: 'leave',
									imgBefore: true,
									imgSrc: '/images/curved-arrow-left-red.svg',
								},
								{
									label: 'Delete Conversation',
									value: 'delete_conversation',
									imgBefore: true,
									imgSrc: '/images/DeleteModal.svg',
									showDivider: true,
								},
							]}
							onSelect={handleOptionSelect}
							mainClassName={styles.headerActions}
							data-cy="groupKebab"
						/>
					</div>

					<div className={styles.messagesContainer}>
						{uploading && uploadingPreview && (
							<div className={styles.messageGroup + ' ' + styles.sent}>
								<div className={styles.messageWrapper}>
									<div className={styles.messageContent}>
										<div className={styles.message}>
											<div className={styles.messageBubble}>
												<div className={styles.fileAttachment}>
													<div className={styles.uploadingMediaWrapper}>
														{uploadingPreview.type.startsWith('image/') ? (
															<img
																src={uploadingPreview.url}
																alt="uploading"
																className={styles.attachedImage + ' ' + styles.uploading}
															/>
														) : (
															<video
																src={uploadingPreview.url}
																className={styles.attachedVideo + ' ' + styles.uploading}
															/>
														)}
														<div className={styles.uploadOverlay}>
															<div className={styles.loaderContainer}>
																<div className={styles.circularLoader}></div>
																<span className={styles.uploadingText}>Uploading...</span>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
									<Avatar
										src={me.profilePic}
										alt="avatar"
										className={styles.messageAvatar}
									/>
								</div>
							</div>
						)}
						{!loading && (
							<>
								{messages.map((message) => {
									const showDateDivider = false

									const time = formatDateAndTimeForChat(
										message.createdAt
									)

									return (
										<div key={message._id}>
											{/* Currently not being used */}
											{showDateDivider && (
												<div
													className={
														styles.dateDivider
													}
												>
													<span>Today</span>
												</div>
											)}

											<div
												className={`${styles.messageGroup} ${message.senderId === me._id ? styles.sent : styles.received}`}
											>
												<div
													className={
														styles.messageWrapper
													}
												>
													{message.senderId !==
														me._id && (
															<div className={styles.avatarWrapper}>
																<Avatar
																	src={
																		groupMembers[
																			message
																				.senderId
																		]?.profilePic
																	}
																	alt="avatar"
																	className={
																		styles.messageAvatar
																	}
																/>
																{onlineUsers.includes(message.senderId) && (
																	<span className={
																		userStatuses[message.senderId] === 'away'
																			? styles.awayIndicator
																			: styles.onlineIndicator
																	}></span>
																)}
															</div>
														)}
													<div
														className={
															styles.messageContent
														}
													>
														{message.senderId !==
															me._id && (
																<span className={styles.senderName}>{message.name}</span>
															)}
														<div
															className={
																styles.message
															}
														>
															<div
																className={
																	styles.messageBubble
																}
															>
																{message.fileUrl && (
																	<div className={styles.fileAttachment}>
																		{message.fileUrl.match(/\.(jpeg|jpg|gif|png|svg)/i) ? (
																			<img
																				src={message.fileUrl}
																				alt="attachment"
																				className={styles.attachedImage}
																				onClick={() => window.open(message.fileUrl, '_blank')}
																			/>
																		) : message.fileUrl.match(/\.(mp4|mov|quicktime)/i) ? (
																			<video
																				controls
																				className={styles.attachedVideo}
																			>
																				<source src={message.fileUrl} />
																				Your browser does not support the video tag.
																			</video>
																		) : (
																			<a
																				href={message.fileUrl}
																				target="_blank"
																				rel="noopener noreferrer"
																				className={styles.fileLink}
																			>
																				<img src="/images/document.svg" alt="file" style={{ width: '24px', height: '24px', filter: message.senderId === me._id ? 'brightness(0) invert(1)' : 'none' }} />
																				<span>View Document</span>
																			</a>
																		)}
																	</div>
																)}
																{message.text}
															</div>
														</div>
														<div
															className={
																styles.messageTime
															}
														>
															{time}
															{message.senderId === me._id && message.status === 'sending' && (
																<span style={{ fontSize: '10px', marginLeft: '5px', opacity: 0.7 }}>• Sending...</span>
															)}
															{message.senderId === me._id && message.status === 'failed' && (
																<span style={{ fontSize: '10px', marginLeft: '5px', color: 'red' }}>• Failed</span>
															)}
														</div>
													</div>
													{message.senderId ===
														me._id && (
															<div className={styles.avatarWrapper}>
																<Avatar
																	src={me.profilePic}
																	alt="avatar"
																	className={
																		styles.messageAvatar
																	}
																/>
																{onlineUsers.includes(me._id) && (
																	<span className={
																		userStatuses[me._id] === 'away'
																			? styles.awayIndicator
																			: styles.onlineIndicator
																	}></span>
																)}
															</div>
														)}
												</div>
											</div>
										</div>
									)
								})}
							</>
						)}
					</div>

					{isFreeTierVendorOrPlanner(accountType, payment) ? (
						<div className={styles.chatInput} style={{ justifyContent: 'center', padding: '16px', backgroundColor: 'var(--bg3)', borderRadius: '12px', margin: '0 24px 24px 24px' }}>
							<p style={{ margin: 0, fontWeight: 600, color: 'var(--secondary-Text)' }}>Upgrade to chat with group</p>
						</div>
					) : (
						<div className={styles.chatInput}
							style={uploading ? {
								opacity: 0.5,
								cursor: 'not-allowed',
								pointerEvents: 'none'
							} : { 
								opacity: 1,
								cursor: 'pointer',
								pointerEvents: 'auto'
							}}
						>
							<div
								className={styles.chatInputAttachBtn}
								onClick={() => fileInputRef.current?.click()}
								style={{ cursor: 'pointer' }}
							>
								<img src="/images/attach.svg" alt="attach" />
							</div>
							<input
								type="file"
								ref={fileInputRef}
								style={{ display: 'none' }}
								onChange={handleFileSelect}
								disabled={uploading}
								accept=".png,.jpeg,.jpg,.gif,.svg,.mp4,.mov,.pdf,.xlsx,.csv"
							/>
							<form
								className={styles.inputWrapper}
								onSubmit={handleSendChat}
							>
								<input
									type="text"
									placeholder={selectedFile ? selectedFile.name : "Type a message"}
									name="message"
									id="message"
									value={text}
									onChange={(e) => setText(e.target.value)}
									data-cy="textfield"
								/>
								<div className={styles.chatInputBtn}>
									<img
										src="/images/ChatSend.svg"
										alt="ChatSend"
										onClick={handleSendChat}
										data-cy="send"
									/>
								</div>
							</form>
						</div>
					)}
				</>
			) : (
				<div className={styles.emptyState}>
					<div className={styles.emptyIcon}>💬</div>
					<h3>Select a conversation</h3>
					<p>
						Choose from your existing conversations or start a new
						one
					</p>
				</div>
			)}
		</>
	)
}
